import type { Shuffler } from "../../shared/lib/pluralistic"
import type { Post } from "~~/prisma/generated/client"
import type { Ref } from "#imports"

function getQueryCursor(posts: Post[], shuffler: Shuffler) {
  // this will assume everything is sorted by date
  const allReceivedPosts = [...posts, ...shuffler.postsQueue]
  return allReceivedPosts.reduce((best, post) => {
    if (post.pinned) {
      // pinned posts are ignored. In fact, they can be very old. If they were not ignored,
      // then posts more recent than them will never be displayed.
      return best
    }
    const curValue = new Date(post.date).getTime()
    return Math.min(best, curValue)
  },
  new Date().getTime())
}

type PaginationMethod = "cursor" | "offset"

/* Returns true IFF it adds more posts */
export const infiniteScrollingHelper = async (
  posts: Ref<Post[]>, api: string, paginationMethod: PaginationMethod, pluralistic: boolean,
  shuffler: Shuffler) => {
  if (!posts.value?.length) {
    return false;
  }
  async function fetch(): Promise<Post[]> {
    const params = new URLSearchParams({ maxitems: "12" })
    if (paginationMethod === "offset") {
      params.set("offset", posts.value?.length?.toString() ?? "0")
    } else { // cursor
      params.set("after", getQueryCursor(posts.value, shuffler).toString())
    }
    const url = `${api}?${params}`
    return await $fetch<Post[]>(url)
  }

  if (!pluralistic) {
    const ret = await fetch()
    console.log("Got (non-pluralistic)", ret?.length, "posts", ret)
    posts.value.push(...ret)
    return ret.length > 0
  }

  // When we're in a pluralistic blob, then it might happen that all the
  // items we fetch are "hidden" by the rate limiter.
  // Therefore, we iterate by asking more items, hoping to find something new.
  for (let i = 1; i <= 10; i++) {
    const ret = await fetch()
    console.log("Got (pluralistic)", ret?.length, "posts", ret)
    if (ret?.length) {
      const shuffled = shuffler.addAndGet(ret)
      console.debug(`Got ${ret.length} items, after shuffling it's ${shuffled.length}`)
      if (shuffled.length) {
        posts?.value.push(...shuffled)
        return true
      }
      else {
        console.warn(`Shuffling make it so that all ${ret.length} went hidden`)
      }
    }
  }

  return false
}
